import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getApiUrl } from '../config';

// Kullanıcının IP adresini almak için yardımcı fonksiyon
const getUserIp = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('IP adresi alınamadı:', error);
    return null;
  }
};

// Aylık ödeme hesaplama yardımcı fonksiyonu
const calculateMonthlyPayment = (totalAmount, installmentCount, installmentRate) => {
  if (installmentCount > 0) {
    return ((totalAmount * (1 + installmentRate / 100)) / installmentCount).toFixed(2);
  }
  return 0;
};

export const paymentApi = createApi({
  reducerPath: 'paymentApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiUrl(),
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');
      
      if (token) {
        headers.set('Authorization', `${token}`);
      }
      if (tkn) {
        headers.set('Tkn', `${tkn}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['Payment', 'Installments', 'CommissionRates'],
  endpoints: (builder) => ({
    // Banka ve komisyon oranlarını getir
    getCommissionRates: builder.query({
      query: () => '/bank_list',
      transformResponse: (data) => {
        if (Array.isArray(data) && data.length > 0) {
          return data.map(bank => {
            return {
              name: bank.name,
              image: bank.image,
              taksit: Object.entries(bank.taksit).map(([count, rate]) => ({
                count: parseInt(count),
                rate: rate,
              }))
            };
          });
        }
        throw new Error('Geçersiz veri formatı');
      },
      providesTags: ['CommissionRates'],
    }),
    
    // Kart bilgisine göre taksit seçeneklerini getir
    getInstallments: builder.mutation({
      query: ({ card_number, total_amount }) => ({
        url: '/card_info',
        method: 'POST',
        body: { card_number },
      }),
      transformResponse: (data, _, arg) => {
        try {
          // Eski context'teki gibi doğrudan taksit bilgilerini döndür
          const totalAmount = arg.total_amount || 0;
          return Object.entries(data.installment_rates || {}).map(
            ([count, rate]) => ({
              count: parseInt(count),
              rate: rate,
              totalAmount: calculateMonthlyPayment(totalAmount, parseInt(count), rate),
            })
          );
        } catch (error) {
          console.error('Taksit dönüştürme hatası:', error);
          return []; // Hata durumunda boş dizi döndür
        }
      },
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          // Facebook Pixel event
          if (typeof fbq === 'function') {
            fbq('track', 'AddPaymentInfo');
          }
        } catch (error) {
          console.error('Taksit bilgisi alınamadı', error);
        }
      },
      invalidatesTags: ['Installments'],
    }),
    
    // Ödeme işlemini başlat
    processPayment: builder.mutation({
      async queryFn(paymentData, _api, _extraOptions, fetchWithBaseQuery) {
        try {
          // Gelen verilerin geçerliliğini kontrol et
          if (!paymentData) {
            throw new Error('Ödeme verileri bulunamadı');
          }
          
          // Müşteri formu kontrolü
          if (paymentData.musteriForm) {
            paymentData.musteriForm = paymentData.musteriForm || {};
          }
          
          // Kullanıcının IP adresini al
          const userIp = await getUserIp();
          
          // Ödeme verisine IP adresini ekle
          const paymentDataWithIp = {
            ...paymentData,
            ipAddress: userIp
          };
          
          const result = await fetchWithBaseQuery({
            url: '/send_order2',
            method: 'POST',
            body: paymentDataWithIp
          });
          
          // Yanıt kontrolü
          if (!result.data) {
            throw new Error('API yanıtı geçersiz format');
          }
          
          if (result.data.success) {
            // Facebook Pixel event
            if (typeof fbq === 'function') {
              fbq('track', 'InitiateCheckout');
            }
            
            return {
              data: {
                success: true,
                redirect_url: result.data.redirect_url,
                orderId: result.data.order_id,
              }
            };
          } else {
            let errorMessage = 'Ödeme işlemi sırasında hata oluştu';
            
            // Özelleştirilmiş hata mesajları
            if (result.data.error_code) {
              switch (result.data.error_code) {
                case 14:
                  errorMessage = 'Geçersiz kart numarası. Lütfen kart numaranızı kontrol ediniz.';
                  break;
                case 12:
                  errorMessage = 'Kart bilgileriniz eksik veya hatalı. Lütfen kart bilgilerinizi kontrol ediniz.';
                  break;
                case 15:
                  errorMessage = 'Geçersiz son kullanma tarihi. Lütfen kartınızın son kullanma tarihini kontrol ediniz.';
                  break;
                case 16:
                  errorMessage = 'Geçersiz güvenlik kodu (CVC). Lütfen kartınızın arkasındaki güvenlik kodunu kontrol ediniz.';
                  break;
                case 17:
                  errorMessage = 'Kartınızda yeterli bakiye bulunmamaktadır.';
                  break;
                case 18:
                  errorMessage = 'Kartınız bu işlem için uygun değildir. Bankanızla iletişime geçiniz.';
                  break;
                case 19:
                  errorMessage = 'Seçtiğiniz taksit sayısı bu kart için geçerli değildir.';
                  break;
                default:
                  errorMessage = result.data.error || 'Ödeme işlemi sırasında beklenmeyen bir hata oluştu. Lütfen tekrar deneyiniz.';
              }
            } else if (!paymentData.taksit_sayisi) {
              errorMessage = 'Lütfen taksit seçeneğini belirtiniz.';
            } else if (!paymentData.musteriForm || !paymentData.musteriForm.faturaAd || !paymentData.musteriForm.faturaSoyad) {
              errorMessage = 'Fatura bilgileriniz eksik veya hatalı. Lütfen kontrol ediniz.';
            }
            
            return {
              error: {
                status: 'CUSTOM_ERROR',
                data: errorMessage
              }
            };
          }
        } catch (error) {
          console.error("Payment Error:", error);
          let errorMessage = 'Ödeme işlemi sırasında bir hata oluştu';
          
          if (error.response) {
            if (error.response.status === 404) {
              errorMessage = 'Kart bilgileri doğrulanamadı. Lütfen bilgilerinizi kontrol ediniz.';
            } else if (error.response.data && error.response.data.error) {
              // API'den dönen hata mesajına göre özelleştirilmiş mesajlar
              switch (error.response.data.error) {
                case "Unexpected Card Number":
                  errorMessage = 'Geçersiz kart numarası. Lütfen kart numaranızı kontrol ediniz.';
                  break;
                case "Invalid Expiry Date":
                  errorMessage = 'Geçersiz son kullanma tarihi. Lütfen kontrol ediniz.';
                  break;
                case "Invalid CVC":
                  errorMessage = 'Geçersiz güvenlik kodu. Lütfen kontrol ediniz.';
                  break;
                case "Insufficient Funds":
                  errorMessage = 'Kartınızda yeterli bakiye bulunmamaktadır.';
                  break;
                case "Card Not Supported":
                  errorMessage = 'Bu kart türü desteklenmiyor. Farklı bir kart ile deneyiniz.';
                  break;
                case "3D Secure Failed":
                  errorMessage = '3D Secure doğrulaması başarısız oldu. Bankanızla iletişime geçebilirsiniz.';
                  break;
                default:
                  errorMessage = error.response.data.error || 'Ödeme işlemi sırasında beklenmeyen bir hata oluştu';
              }
            }
          }
          
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: errorMessage
            }
          };
        }
      },
      invalidatesTags: ['Payment'],
    }),
    
    // Ödeme sonucu bildirimi
    paymentResult: builder.mutation({
      async queryFn(allParams, _api, _extraOptions, fetchWithBaseQuery) {
        try {
          // allParams parametresinin varlığını kontrol et
          const params = allParams || {};
          
          // Eğer ipAddress parametresi yoksa, kullanıcının IP adresini al
          let paramsWithIp = { ...params };
          if (!paramsWithIp.ipAddress) {
            const userIp = await getUserIp();
            paramsWithIp = { ...paramsWithIp, ipAddress: userIp };
          }
          
          const result = await fetchWithBaseQuery({
            url: '/payment/success',
            method: 'POST',
            body: paramsWithIp
          });
          
          if (result.error) {
            return { error: result.error };
          }
          
          return { data: result.data };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: error.message || 'Veri gönderilirken bir hata oluştu'
            }
          };
        }
      },
    }),
    
    // EFT ödeme bildirimi
    confirmEftPayment: builder.mutation({
      async queryFn({ orderNumber, orderAmount }, _api, _extraOptions, fetchWithBaseQuery) {
        try {
          if (!orderNumber) {
            throw new Error('Sipariş numarası (Order ID) gereklidir');
          }

          const orderNumberStr = String(orderNumber);
          const orderId = orderNumberStr.startsWith('EFT-') ? orderNumberStr.substring(4) : orderNumberStr;

          // Kullanıcının IP adresini al
          const userIp = await getUserIp();

          const data = {
            order: orderId,
            tutar: orderAmount,
            ipAddress: userIp
          };

          const result = await fetchWithBaseQuery({
            url: '/payment/success_eft',
            method: 'POST',
            body: data
          });
          
          if (result.error) {
            return { error: result.error };
          }
          
          if (result.data.success) {
            // Facebook Pixel event
            if (typeof fbq === 'function') {
              fbq('track', 'Purchase', {
                currency: 'TRY',
                value: result.data.price
              });
            }
            
            return {
              data: {
                success: true,
                price: result.data.price,
                message: 'EFT ödemeniz başarıyla bildirildi.'
              }
            };
          } else {
            const errorMessage = result.data.message || 'EFT bildirimi yapılırken bir hata oluştu';
            
            return {
              error: {
                status: 'CUSTOM_ERROR',
                data: errorMessage
              }
            };
          }
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: error.message || 'EFT bildirimi yapılırken bir hata oluştu'
            }
          };
        }
      },
      invalidatesTags: ['Payment'],
    }),
  }),
});

export const {
  useGetCommissionRatesQuery,
  useGetInstallmentsMutation,
  useProcessPaymentMutation,
  usePaymentResultMutation,
  useConfirmEftPaymentMutation,
} = paymentApi;

export { calculateMonthlyPayment }; 